import { FC, memo, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import Select from '../atom/Select';
import { FilterSectionFieldsProps } from '../../models';
import type { IState } from '../../../../models';
import { filter_mervs, filter_types } from '../../helpers/data/bot';

const FilterSectionFields: FC<FilterSectionFieldsProps> = ({
  section,
  onFieldChange,
}) => {
  // Get filter parameters from redux store
  const params = useSelector((state: IState) => state.filter.filter_params);

  /**
   * Filter allowed nominal depths
   * Only specific depth values are supported by business rules
   */
  const nominal_depth = useMemo(() => {
    return params?.nominal_depth.filter(
      (e) =>
        e.value === 1 ||
        e.value === 2 ||
        e.value === 4 ||
        e.value === 12 ||
        e.value === 15 ||
        e.value === 22 ||
        e.value === 30,
    );
  }, [params?.nominal_depth]);

  /**
   * Calculate available MERV and type options
   * based on selected nominal depth
   */
  const merv_type = useMemo(() => {
    // Find selected nominal depth value
    const depth_nominal_id = nominal_depth?.find(
      (e) => e.id === section.depth_nominal_id,
    )?.value as number;

    // If no depth selected, return empty options
    if (!depth_nominal_id)
      return {
        merv: [],
        type: [],
      };

    return {
      // Filter MERVs allowed for selected depth
      merv: params?.merv.filter((e) =>
        filter_mervs[depth_nominal_id].find((m) => m === e.id),
      ),

      // Filter types allowed for selected depth
      type: params?.type.filter((e) =>
        filter_types[depth_nominal_id].find((m) => m === e.id),
      ),
    };
  }, [section.depth_nominal_id, nominal_depth]);

  /**
   * Ensure selected nominal depth is valid
   * Auto-select a default if current value is missing or invalid
   */
  useEffect(() => {
    if (nominal_depth && nominal_depth.length > 0) {
      if (!nominal_depth.find((m) => m.id === section.depth_nominal_id)) {
        onFieldChange(
          (nominal_depth[1] || nominal_depth[0]).id,
          'depth_nominal_id',
        );
      }
    }
  }, [nominal_depth]);

  /**
   * Ensure selected MERV and type are valid
   * Auto-correct when nominal depth changes
   */
  useEffect(() => {
    if (merv_type.merv && merv_type.merv.length > 0) {
      if (!merv_type.merv.find((m) => m.id === section.merv_id)) {
        onFieldChange(merv_type.merv[0].id, 'merv_id');
      }
    }

    if (merv_type.type && merv_type.type.length > 0) {
      if (!merv_type.type.find((m) => m.id === section.type_id)) {
        onFieldChange(merv_type.type[0].id, 'type_id');
      }
    }
  }, [merv_type]);

  return (
    <div className="bot-section__field-group">
      <div className="bot-section__field">
        <span className="text-span">with</span>
        <Select
          name="depth_nominal_id"
          value={section.depth_nominal_id ?? ''}
          options={nominal_depth}
          onChange={onFieldChange}
          className="bot-section__select"
          border_bottom={false}
        />
        <span className="text-span">in deep,</span>
      </div>

      <div className="bot-section__field">
        <span className="text-span with-padding-left">MERV</span>
        <Select
          name="merv_id"
          value={section.merv_id ?? ''}
          options={merv_type.merv}
          onChange={onFieldChange}
          className="bot-section__select"
          border_bottom={false}
        />
        <span className="text-span with-padding-right with-comma">,</span>
      </div>

      <div className="bot-section__field">
        <Select
          name="type_id"
          value={section.type_id ?? ''}
          options={merv_type.type}
          onChange={onFieldChange}
          className="bot-section__select"
          isLowerCase={true}
          border_bottom={false}
        />
        <span className="text-span with-padding-right with-comma">,</span>
      </div>

      <div className="bot-section__field">
        <Select
          name="frame_loading_id"
          value={section.frame_loading_id ?? ''}
          options={params?.frame.loading}
          onChange={onFieldChange}
          className="bot-section__select"
          isLowerCase={true}
          border_bottom={false}
        />
        <span className="text-span with-padding-right">loading filter,</span>
      </div>

      <div className="bot-section__field">
        <Select
          name="air_stream_id"
          value={section.air_stream_id ?? ''}
          options={params?.air_stream}
          onChange={onFieldChange}
          className="bot-section__select"
          isLowerCase={true}
          border_bottom={false}
          disabled
        />
        <span className="text-span">airstream</span>
      </div>
    </div>
  );
};

export default memo(FilterSectionFields);
